<template>
  <div class="app-forms">
    <div class="vx-row">
      <div class="vx-col w-full">
        <vx-card title="Application Forms" subtitle="Real forms used throughout the TCVelik application">
          
          <div class="space-y-8">
            
            <!-- User Invitation Form -->
            <div class="form-example">
              <h3 class="text-lg font-semibold mb-4">User Invitation Form</h3>
              <p class="text-gray-600 mb-4">Form used to invite new users to the system with role assignment.</p>
              <vs-button @click="showInviteModal = true" color="primary">Open Invite User Modal</vs-button>
              
              <vs-popup title="Invite User" :active.sync="showInviteModal" class="invite-user-modal">
                <div class="invite-user-content p-4">
                  <div class="vx-row">
                    <div class="vx-col w-full">
                      <vs-input
                        v-model="inviteForm.email"
                        placeholder="user@example.com"
                        type="email"
                        class="w-full mb-4"
                        label="Email Address"
                        :danger="inviteErrors.email"
                        :danger-text="inviteErrors.email">
                      </vs-input>
                    </div>
                  </div>

                  <div class="vx-row">
                    <div class="vx-col w-full">
                      <div class="chip-container">
                        <v-select 
                          multiple
                          label="name"
                          :closeOnSelect="false"
                          v-model="inviteForm.roles"
                          taggable
                          push-tags
                          :options="availableRoles"
                          placeholder="Select Roles"
                          class="w-full mb-4"
                          :class="{ 'is-invalid': inviteErrors.roleId }"
                          @option:created="createRole">
                        </v-select>
                        <div v-if="inviteErrors.roleId" class="text-danger text-sm mt-1">{{ inviteErrors.roleId }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="vx-row">
                    <div class="vx-col w-full">
                      <vs-textarea
                        v-model="inviteForm.customMessage"
                        placeholder="Optional: Add a personal message..."
                        class="w-full mb-4"
                        label="Custom Message (Optional)"
                        rows="3">
                      </vs-textarea>
                    </div>
                  </div>

                  <div class="flex justify-end space-x-2">
                    <vs-button @click="showInviteModal = false" color="warning" type="border">Cancel</vs-button>
                    <vs-button @click="submitInviteForm" color="primary">Send Invitation</vs-button>
                  </div>
                </div>
              </vs-popup>
            </div>

            <!-- Todo Creation Form -->
            <div class="form-example">
              <h3 class="text-lg font-semibold mb-4">Todo Creation Form</h3>
              <p class="text-gray-600 mb-4">Form used to create new todo items with due dates and priority settings.</p>
              <vs-button @click="showTodoModal = true" color="success">Open Add Todo Modal</vs-button>
              
              <vs-prompt
                vs-title="Add Task"
                vs-accept-text="Add Task"
                vs-button-cancel="border"
                @vs-cancel="clearTodoForm"
                @vs-accept="submitTodoForm"
                @vs-close="clearTodoForm"
                :vs-is-valid="isTodoFormValid"
                :vs-active.sync="showTodoModal">
                
                <div class="todo-form">
                  <div class="vx-row mb-4">
                    <div class="vx-col w-full">
                      <vs-input
                        v-model="todoForm.title"
                        placeholder="Enter task title"
                        label="Task Title"
                        class="w-full">
                      </vs-input>
                    </div>
                  </div>
                  
                  <div class="vx-row mb-4">
                    <div class="vx-col w-full">
                      <vs-textarea
                        v-model="todoForm.description"
                        placeholder="Enter task description"
                        label="Description"
                        rows="3"
                        class="w-full">
                      </vs-textarea>
                    </div>
                  </div>
                  
                  <div class="vx-row mb-4">
                    <div class="vx-col w-full">
                      <datepicker
                        v-model="todoForm.dueDate"
                        placeholder="Select due date"
                        label="Due Date"
                        class="w-full">
                      </datepicker>
                    </div>
                  </div>
                  
                  <div class="vx-row mb-4">
                    <div class="vx-col w-full">
                      <vs-select
                        v-model="todoForm.priority"
                        placeholder="Select priority"
                        label="Priority"
                        class="w-full">
                        <vs-select-item value="low" text="Low" />
                        <vs-select-item value="medium" text="Medium" />
                        <vs-select-item value="high" text="High" />
                        <vs-select-item value="urgent" text="Urgent" />
                      </vs-select>
                    </div>
                  </div>
                  
                  <div class="vx-row">
                    <div class="vx-col w-full">
                      <div class="flex items-center space-x-4">
                        <vs-checkbox v-model="todoForm.isImportant">Important</vs-checkbox>
                        <vs-checkbox v-model="todoForm.isStarred">Starred</vs-checkbox>
                      </div>
                    </div>
                  </div>
                </div>
              </vs-prompt>
            </div>

            <!-- Filter Creation Form -->
            <div class="form-example">
              <h3 class="text-lg font-semibold mb-4">Filter Creation Form</h3>
              <p class="text-gray-600 mb-4">Form used to create custom filters for data views with dynamic field selection.</p>
              <vs-button @click="showFilterModal = true" color="dark">Open Create Filter Modal</vs-button>
              
              <vs-popup title="Create Filter" :active.sync="showFilterModal">
                <div class="con-exemple-prompt p-4">
                  <vs-input
                    v-model="filterForm.name"
                    placeholder="Filter Name (e.g., Sitefinity Login)"
                    label="Filter Name"
                    class="mb-4">
                  </vs-input>
                  
                  <vs-input
                    v-model="filterForm.description"
                    placeholder="Description"
                    label="Description"
                    class="mb-4">
                  </vs-input>
                  
                  <vs-select
                    v-model="filterForm.fieldName"
                    placeholder="Select Field"
                    label="Field"
                    class="mb-4">
                    <vs-select-item 
                      v-for="(field, index) in filterFields" 
                      :key="index"
                      :value="field.Name" 
                      :text="field.Name" />
                  </vs-select>
                  
                  <vs-select
                    v-model="filterForm.operation"
                    placeholder="Select Operation"
                    label="Operation"
                    class="mb-4">
                    <vs-select-item 
                      v-for="op in getOperationsForField(filterForm.fieldName)" 
                      :key="op"
                      :value="op" 
                      :text="op" />
                  </vs-select>
                  
                  <vs-input
                    v-model="filterForm.value"
                    placeholder="Value"
                    label="Value"
                    class="mb-4">
                  </vs-input>
                  
                  <vs-input
                    v-if="filterForm.operation === 'between'"
                    v-model="filterForm.value2"
                    placeholder="Second Value"
                    label="Second Value"
                    class="mb-4">
                  </vs-input>
                  
                  <div class="flex justify-end space-x-2">
                    <vs-button @click="showFilterModal = false" color="warning" type="border">Cancel</vs-button>
                    <vs-button @click="submitFilterForm" color="primary">Create Filter</vs-button>
                  </div>
                </div>
              </vs-popup>
            </div>

            <!-- Signal Creation Form -->
            <div class="form-example">
              <h3 class="text-lg font-semibold mb-4">Test Signal Creation Form</h3>
              <p class="text-gray-600 mb-4">Form used to create test signals for system integration testing.</p>
              <vs-button @click="showSignalModal = true" color="warning">Open Create Signal Modal</vs-button>
              
              <vs-popup title="Create Test Signal" :active.sync="showSignalModal" class="holamundo">
                <div class="con-exemple-prompt p-4">
                  <vs-input
                    v-model="signalForm.system"
                    placeholder="Sitefinity"
                    label="System"
                    class="mb-4">
                  </vs-input>
                  
                  <vs-input
                    v-model="signalForm.action"
                    placeholder="PageOpen"
                    label="Action"
                    class="mb-4">
                  </vs-input>
                  
                  <vs-input
                    v-model="signalForm.userEmail"
                    placeholder="user@example.com"
                    label="User Email"
                    class="mb-4">
                  </vs-input>
                  
                  <vs-input
                    v-model="signalForm.context"
                    placeholder="/settings/permissions"
                    label="Context"
                    class="mb-4">
                  </vs-input>
                  
                  <vs-input
                    v-model="signalForm.sopHint"
                    placeholder="sitefinity-settings"
                    label="SOP Hint"
                    class="mb-4">
                  </vs-input>
                  
                  <div class="flex justify-end space-x-2">
                    <vs-button @click="showSignalModal = false" color="warning" type="border">Cancel</vs-button>
                    <vs-button @click="submitSignalForm" color="primary">Create Signal</vs-button>
                  </div>
                </div>
              </vs-popup>
            </div>

          </div>

        </vx-card>
      </div>
    </div>
  </div>
</template>

<script>
import VxCard from '@/components/vx-card/VxCard.vue'
import Datepicker from 'vuejs-datepicker'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  name: 'AppForms',
  components: {
    VxCard,
    Datepicker,
    vSelect
  },
  data() {
    return {
      showInviteModal: false,
      showTodoModal: false,
      showFilterModal: false,
      showSignalModal: false,
      
      inviteForm: {
        email: '',
        roles: [],
        customMessage: ''
      },
      
      inviteErrors: {
        email: '',
        roleId: ''
      },
      
      availableRoles: [
        { name: 'Admin', value: 'admin' },
        { name: 'Editor', value: 'editor' },
        { name: 'Guest', value: 'guest' },
        { name: 'Public', value: 'public' }
      ],
      
      todoForm: {
        title: '',
        description: '',
        dueDate: null,
        priority: 'medium',
        isImportant: false,
        isStarred: false
      },
      
      filterForm: {
        name: '',
        description: '',
        fieldName: '',
        operation: '',
        value: '',
        value2: ''
      },
      
      filterFields: [
        { Name: 'System' },
        { Name: 'Action' },
        { Name: 'UserEmail' },
        { Name: 'Context' },
        { Name: 'SOPHint' },
        { Name: 'Processed' },
        { Name: 'Date' }
      ],
      
      signalForm: {
        system: 'Sitefinity',
        action: 'PageOpen',
        userEmail: 'test@example.com',
        context: '/settings/permissions',
        sopHint: 'sitefinity-settings'
      }
    }
  },
  computed: {
    isTodoFormValid() {
      return this.todoForm.title && this.todoForm.dueDate
    }
  },
  methods: {
    submitInviteForm() {
      this.validateInviteForm()
      
      if (this.isInviteFormValid()) {
        console.log('Invite form submitted:', this.inviteForm)
        this.showInviteModal = false
        this.$vs.notify({
          title: 'Success',
          text: 'User invitation sent successfully',
          color: 'success'
        })
      } else {
        this.$vs.notify({
          title: 'Validation Error',
          text: 'Please fix the errors and try again',
          color: 'danger'
        })
      }
    },
    
    validateInviteForm() {
      this.inviteErrors = { email: '', roleId: '' }
      
      if (!this.inviteForm.email) {
        this.inviteErrors.email = 'Email is required'
      } else if (!this.isValidEmail(this.inviteForm.email)) {
        this.inviteErrors.email = 'Please enter a valid email'
      }
      
      if (!this.inviteForm.roles || this.inviteForm.roles.length === 0) {
        this.inviteErrors.roleId = 'At least one role is required'
      }
    },
    
    isInviteFormValid() {
      return !Object.values(this.inviteErrors).some(error => error !== '')
    },
    
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },
    
    createRole(newRole) {
      this.availableRoles.push({ name: newRole, value: newRole.toLowerCase() })
    },
    
    submitTodoForm() {
      console.log('Todo form submitted:', this.todoForm)
      this.$vs.notify({
        title: 'Success',
        text: 'Todo created successfully',
        color: 'success'
      })
    },
    
    clearTodoForm() {
      this.todoForm = {
        title: '',
        description: '',
        dueDate: null,
        priority: 'medium',
        isImportant: false,
        isStarred: false
      }
    },
    
    submitFilterForm() {
      console.log('Filter form submitted:', this.filterForm)
      this.showFilterModal = false
      this.$vs.notify({
        title: 'Success',
        text: 'Filter created successfully',
        color: 'success'
      })
    },
    
    getOperationsForField(fieldName) {
      const operations = {
        'System': ['equals', 'contains', 'startsWith', 'endsWith'],
        'Action': ['equals', 'contains', 'startsWith', 'endsWith'],
        'UserEmail': ['equals', 'contains', 'startsWith', 'endsWith'],
        'Context': ['equals', 'contains', 'startsWith', 'endsWith'],
        'SOPHint': ['equals', 'contains', 'startsWith', 'endsWith'],
        'Processed': ['equals', 'not equals'],
        'Date': ['equals', 'before', 'after', 'between']
      }
      
      return operations[fieldName] || ['equals', 'contains']
    },
    
    submitSignalForm() {
      console.log('Signal form submitted:', this.signalForm)
      this.showSignalModal = false
      this.$vs.notify({
        title: 'Success',
        text: 'Test signal created successfully',
        color: 'success'
      })
    }
  }
}
</script>

<style scoped>
.form-example {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1.5rem;
  background-color: #f9fafb;
}

.invite-user-modal {
  max-width: 28rem;
}

.todo-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
